import React from 'react';
import { motion } from 'framer-motion';
import { GlobeIcon, ServerIcon, ShieldCheckIcon, ZapIcon, ArrowRightIcon, CheckCircle2Icon, HeadphonesIcon, LockIcon, LayoutTemplateIcon } from 'lucide-react';
const features = [{
  icon: GlobeIcon,
  title: 'Domain Registration',
  desc: 'Secure your perfect web address with easy registration, auto-renewal, and DNS management included.'
}, {
  icon: ServerIcon,
  title: 'High-Performance Hosting',
  desc: 'Lightning-fast load times with our optimized servers, ensuring your website is always responsive.'
}, {
  icon: ShieldCheckIcon,
  title: '99.9% Uptime Guarantee',
  desc: 'Enterprise-grade reliability keeps your business online and accessible to customers around the clock.'
}, {
  icon: LockIcon,
  title: 'Free SSL Certificates',
  desc: 'Protect your visitors and boost your SEO rankings with included SSL encryption for every site.'
}, {
  icon: ZapIcon,
  title: '1-Click Installations',
  desc: 'Easily install WordPress, Joomla, and other popular applications with a single click.'
}, {
  icon: HeadphonesIcon,
  title: '24/7 Expert Support',
  desc: 'Our dedicated technical team is always available to help you resolve any hosting or domain issues.'
}];
const plans = [{
  name: 'Starter Hosting',
  price: '$9.99',
  period: '/mo',
  desc: 'Perfect for small business websites and blogs.',
  features: ['1 Website', '50 GB SSD Storage', 'Unmetered Bandwidth', 'Free SSL Certificate', 'Free Domain for 1st Year']
}, {
  name: 'Business Pro',
  price: '$19.99',
  period: '/mo',
  desc: 'Ideal for growing businesses and e-commerce.',
  features: ['Unlimited Websites', 'Unlimited SSD Storage', 'Unmetered Bandwidth', 'Free SSL Certificate', 'Free Domain for 1st Year', 'Daily Backups'],
  popular: true
}];
export function DomainsHostingPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div className="pt-20" data-id="element-1234">
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden bg-[#011B33]" data-id="element-1235">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-1236"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-full bg-gradient-to-b from-transparent to-[#011B33]" data-id="element-1237"></div>
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#22A39F]/20 rounded-full blur-[120px] pointer-events-none" data-id="element-1238"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1239">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-1240">
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#22A39F]/10 border border-[#22A39F]/20 text-[#22A39F] text-sm font-bold mb-8" data-id="element-1241">
              <ServerIcon className="w-4 h-4 mr-2" data-id="element-1242" />
              Reliable Web Infrastructure
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-1243">
              Premium Domains & <br data-id="element-1244" />
              <span className="text-[#22A39F]" data-id="element-1245">High-Speed Hosting</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed mb-10" data-id="element-1246">
              Build your digital presence on a rock-solid foundation. Fast,
              secure, and reliable hosting paired with the perfect domain name
              for your business.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-1247">
              <button onClick={() => onNavigate('contact')} className="w-full sm:w-auto bg-[#22A39F] hover:bg-[#1d8a87] text-white px-8 py-4 rounded-full font-bold text-lg transition-all shadow-lg shadow-[#22A39F]/20 flex items-center justify-center" data-id="element-1248">
                Get Started Today
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1249" />
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Domain Search Section */}
      <section className="py-20 bg-[#F8FAFC]" data-id="element-1250">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1251">
          <h2 className="text-4xl md:text-5xl font-extrabold text-[#011B33] mb-4" data-id="element-1252">
            Find Your Digital Home
          </h2>
          <p className="text-lg text-slate-500 mb-10 max-w-2xl mx-auto" data-id="element-1253">
            Secure your domain and choose a hosting plan built for enterprise
            performance.
          </p>

          <div className="relative max-w-3xl mx-auto" data-id="element-1254">
            <div className="flex items-center bg-white rounded-2xl p-2 shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100" data-id="element-1255">
              <div className="pl-4 pr-2 text-slate-400" data-id="element-1256">
                <LayoutTemplateIcon className="w-6 h-6" data-id="element-1257" />
              </div>
              <input type="text" placeholder="search-your-dream-domain.com" className="flex-1 bg-transparent border-none focus:ring-0 text-lg text-slate-700 placeholder-slate-400 px-2 py-4 outline-none" data-id="element-1258" />
              <button className="bg-[#22A39F] hover:bg-[#1d8a87] text-white font-bold text-lg px-8 py-4 rounded-xl transition-colors" data-id="element-1259">
                Search
              </button>
            </div>

            <div className="flex flex-wrap justify-center gap-6 mt-6 text-sm font-bold text-slate-400" data-id="element-1260">
              <span data-id="element-1261">.COM $12.99</span>
              <span data-id="element-1262">.NET $14.99</span>
              <span data-id="element-1263">.ORG $15.99</span>
              <span data-id="element-1264">.AI $59.99</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white" data-id="element-1265">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1266">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1267">
            <h2 className="text-3xl md:text-4xl font-bold text-[#011B33] mb-4" data-id="element-1268">
              Everything You Need to Succeed Online
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1269">
              Our hosting packages come fully loaded with the tools and
              performance your business demands.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1270">
            {features.map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow" data-id="element-1271">
                <div className="w-14 h-14 bg-[#22A39F]/10 rounded-xl flex items-center justify-center mb-6" data-id="element-1272">
                  <feature.icon className="w-7 h-7 text-[#22A39F]" data-id="element-1273" />
                </div>
                <h3 className="text-xl font-bold text-[#011B33] mb-3" data-id="element-1274">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-1275">{feature.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-white" data-id="element-1276">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1277">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1278">
            <h2 className="text-3xl md:text-4xl font-bold text-[#011B33] mb-4" data-id="element-1279">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-slate-600" data-id="element-1280">
              Choose the plan that fits your business needs. No hidden fees.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-1281">
            {plans.map((plan, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className={`relative bg-white rounded-3xl p-8 md:p-10 border ${plan.popular ? 'border-[#22A39F] shadow-xl shadow-[#22A39F]/10' : 'border-slate-200 shadow-sm'}`} data-id="element-1282">
                {plan.popular && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#22A39F] text-white px-4 py-1 rounded-full text-sm font-bold tracking-wider uppercase" data-id="element-1283">
                    Most Popular
                  </div>}
                <h3 className="text-2xl font-bold text-[#011B33] mb-2" data-id="element-1284">
                  {plan.name}
                </h3>
                <p className="text-slate-500 mb-6" data-id="element-1285">{plan.desc}</p>
                <div className="flex items-baseline gap-1 mb-8" data-id="element-1286">
                  <span className="text-5xl font-extrabold text-[#011B33]" data-id="element-1287">
                    {plan.price}
                  </span>
                  <span className="text-slate-500 font-medium" data-id="element-1288">
                    {plan.period}
                  </span>
                </div>
                <ul className="space-y-4 mb-8" data-id="element-1289">
                  {plan.features.map((feature, idx) => <li key={idx} className="flex items-center gap-3 text-slate-600" data-id="element-1290">
                      <CheckCircle2Icon className="w-5 h-5 text-[#22A39F] shrink-0" data-id="element-1291" />
                      <span data-id="element-1292">{feature}</span>
                    </li>)}
                </ul>
                <button onClick={() => onNavigate('contact')} className={`w-full py-4 rounded-xl font-bold text-lg transition-colors ${plan.popular ? 'bg-[#22A39F] hover:bg-[#1d8a87] text-white' : 'bg-slate-100 hover:bg-slate-200 text-[#011B33]'}`} data-id="element-1293">
                  Select Plan
                </button>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#011B33] relative overflow-hidden" data-id="element-1294">
        <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-1295"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-1296">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1297">
            Ready to get your business online?
          </h2>
          <p className="text-xl text-slate-300 mb-10" data-id="element-1298">
            Contact us today to secure your domain and set up your hosting
            environment.
          </p>
          <button onClick={() => onNavigate('contact')} className="bg-[#22A39F] hover:bg-[#1d8a87] text-white px-10 py-4 rounded-full font-bold text-lg transition-all shadow-lg inline-flex items-center" data-id="element-1299">
            Contact Sales
            <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1300" />
          </button>
        </div>
      </section>
    </div>;
}